import React, { useState, useEffect, useCallback } from 'react';
import {
 getHeroContent, StoreLogo, CartBtn, TabContent, StoreFooter, getTabLabel, AWS_URL, FaHeart, InlineEditableImage
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#d4af37] focus:ring-2 focus:ring-[#d4af37]/20 dark:border-slate-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("fullstory") || field.toLowerCase().includes("story") || field.toLowerCase().includes("bio");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function AuraLayout({ p }) {
  const T = p.storeTheme || {};
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeJewelryIndex, setActiveJewelryIndex] = useState(0);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  // Premium jewelry hero images with editable fields
  const jewelryHeroImages = [
    { field: "heroImage1", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d1b7ca65-3608-4f41-9bdd-c71059e21ff1_6.jpg" },
    { field: "heroImage2", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ff5a1850-a85b-49da-bffb-9a0aceef83aa_7.jpg" },
    { field: "heroImage3", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b5ca2a51-93bd-43c9-9c68-235380b449ce_8.jpg" },
    { field: "heroImage4", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/66ebd0c7-2c2e-433f-b3f1-93167bd228de_9.jpg" },
    { field: "heroImage5", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3522bebe-ac94-4e2e-b756-dc328f095aa9_10.jpg" },
    { field: "heroImage6", src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c6b61742-9330-4a3c-8827-4205999a326b_11.jpg" },
  ];
  
  // Auto-rotate jewelry images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveJewelryIndex((prev) => (prev + 1) % jewelryHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [jewelryHeroImages.length]);
  
  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Navigate to page - normal navigation
  const navigateTo = useCallback((page, category = null, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "collections" || category) {
      p.setActiveTab("products");
      setTimeout(() => {
        const productsSection = document.getElementById("products-section");
        if (productsSection) productsSection.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [p]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "collections") {
      p.setActiveTab("products");
      setTimeout(() => {
        const productsSection = document.getElementById("products-section");
        if (productsSection) productsSection.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [p]);
  
  const copy = getHeroContent(p, {
    eyebrow: "Aura Fine Jewelry",
    title: "Eternal brilliance, crafted for you",
    subtitle: "Discover our exquisite collection of handcrafted jewelry, where every piece tells a story of timeless elegance and unmatched craftsmanship.",
    primaryCta: "Discover Collection",
    secondaryCta: "Our Craftsmanship",
  });
  
  // Create sparkle particles
  const sparkles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 3,
    duration: 1 + Math.random() * 2,
  }));
  
  // Category cards for jewelry with editable fields AND editable images
  const jewelryCategories = [
    { 
      key: "category_rings",
      name: "Rings", 
      icon: "💍", 
      descriptionKey: "category_rings_desc",
      description: "Engagement & wedding bands", 
      imageField: "category_rings_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d1b7ca65-3608-4f41-9bdd-c71059e21ff1_6.jpg",
      page: "rings"
    },
    { 
      key: "category_necklaces",
      name: "Necklaces", 
      icon: "📿", 
      descriptionKey: "category_necklaces_desc",
      description: "Pendants & chains", 
      imageField: "category_necklaces_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ff5a1850-a85b-49da-bffb-9a0aceef83aa_7.jpg",
      page: "necklaces"
    },
    { 
      key: "category_earrings",
      name: "Earrings", 
      icon: "✨", 
      descriptionKey: "category_earrings_desc",
      description: "Studs & drops", 
      imageField: "category_earrings_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/66ebd0c7-2c2e-433f-b3f1-93167bd228de_9.jpg",
      page: "earrings"
    },
    { 
      key: "category_bracelets",
      name: "Bracelets", 
      icon: "💫", 
      descriptionKey: "category_bracelets_desc",
      description: "Bangles & cuffs", 
      imageField: "category_bracelets_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b5ca2a51-93bd-43c9-9c68-235380b449ce_8.jpg",
      page: "bracelets"
    },
  ];

  // Get editable categories with images
  const editableCategories = jewelryCategories.map((category) => ({
    ...category,
    image: p.storeTheme?.[category.imageField] || category.image,
  }));
  
  // Navigation items with editable fields
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_collections", label: "COLLECTIONS", page: "collections" },
    { key: "nav_rings", label: "RINGS", page: "rings" },
    { key: "nav_necklaces", label: "NECKLACES", page: "necklaces" },
    { key: "nav_earrings", label: "EARRINGS", page: "earrings" },
    { key: "nav_bracelets", label: "BRACELETS", page: "bracelets" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    { key: "top_bar_message", value: "COMPLIMENTARY GIFT BOX WITH EVERY PURCHASE" }
  ];

  // Tab items
  const tabItems = [
    { key: "products", labelKey: "tab_products_label", label: "COLLECTION" },
    { key: "services", labelKey: "tab_services_label", label: "SERVICES" },
    { key: "details", labelKey: "tab_details_label", label: "STORE INFO" },
  ];

  // About page stats
  const aboutStats = [
    { valueKey: "about_stat1_value", value: "25+", labelKey: "about_stat1_label", label: "Years of Excellence" },
    { valueKey: "about_stat2_value", value: "100%", labelKey: "about_stat2_label", label: "Ethically Sourced" },
    { valueKey: "about_stat3_value", value: "50+", labelKey: "about_stat3_label", label: "Master Artisans" },
    { valueKey: "about_stat4_value", value: "15+", labelKey: "about_stat4_label", label: "Countries Served" },
  ];

  // About page image config
  const aboutImageConfig = {
    field: "aboutImage",
    src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/375421f1-e665-4ae2-9e2c-41c9a928e120_12.jpg",
  };
  
  // Filter products by category
  const getProductsByCategory = (category) => {
    return (p.filteredProducts || []).filter(product => 
      product.category?.toLowerCase() === category.toLowerCase() ||
      product.productType?.toLowerCase() === category.toLowerCase()
    );
  };
  
  const ringsProducts = getProductsByCategory("rings");
  const necklacesProducts = getProductsByCategory("necklaces");
  const earringsProducts = getProductsByCategory("earrings");
  const braceletsProducts = getProductsByCategory("bracelets");

  // Get display products based on active page
  const getDisplayProducts = () => {
    if (activePage === "collections") {
      return (p.filteredProducts || []).slice(0, 8);
    } else if (activePage === "rings") {
      return ringsProducts.slice(0, 8);
    } else if (activePage === "necklaces") {
      return necklacesProducts.slice(0, 8);
    } else if (activePage === "earrings") {
      return earringsProducts.slice(0, 8);
    } else if (activePage === "bracelets") {
      return braceletsProducts.slice(0, 8);
    }
    return [];
  };

  const displayProducts = getDisplayProducts();
  
  return (
    <div key={refreshKey} className="min-h-screen flex flex-col bg-[#0a0a12] overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Cormorant Garamond', Georgia, serif" }}>
      
      {/* Animated Sparkle Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08)_0%,_transparent_70%)]" />
        {sparkles.map((sparkle) => (
          <div
            key={sparkle.id}
            className="absolute w-1 h-1 bg-[#d4af37] rounded-full animate-twinkle"
            style={{
              left: `${sparkle.left}%`,
              top: `${sparkle.top}%`,
              animationDelay: `${sparkle.delay}s`,
              animationDuration: `${sparkle.duration}s`,
            }}
          />
        ))}
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>
      
      {/* Top Bar - Editable */}
      <div className="relative bg-gradient-to-r from-[#1a1a2e] via-[#d4af37] to-[#1a1a2e] text-white py-2 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 text-center text-sm font-light tracking-wider flex items-center justify-center gap-3">
          <span className="animate-pulse">✨</span>
          <InlineEditableTextWithNav 
            p={p} 
            field="top_bar_message" 
            value="COMPLIMENTARY GIFT BOX WITH EVERY PURCHASE" 
            onNavigate={handleNavigateAfterEdit} 
            navPage="home" 
          />
          <span className="animate-pulse delay-1000">✨</span>
        </div>
      </div>
      
      {/* Luxury Navigation with Working Links */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[#0a0a12]/95 backdrop-blur-xl shadow-2xl py-2" : "bg-gradient-to-b from-black/60 to-transparent py-4"}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo - Clickable to Home */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", null, e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-[#d4af37]" : "border-[#d4af37]/50"}`} />
            <div className="hidden sm:block">
              <p className={`font-bold tracking-wider transition-colors duration-300 ${isScrolled ? "text-[#d4af37]" : "text-white"}`}>
                <InlineEditableTextWithNav 
                  p={p} 
                  field="businessName" 
                  value={p.storeInfo?.businessName || "Aura Jewelry"} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </p>
              <p className="text-[10px] tracking-[0.2em] text-[#d4af37]/70">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="business_subtitle" 
                  value="FINE JEWELRY" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </p>
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors"
          >
            <svg className={`w-6 h-6 ${isScrolled ? "text-[#d4af37]" : "text-white"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, null, e)}
                className={`text-xs tracking-[0.15em] font-light transition-all duration-300 hover:tracking-[0.2em] ${activePage === item.page ? "text-[#d4af37]" : (isScrolled ? "text-gray-300 hover:text-[#d4af37]" : "text-white/80 hover:text-white")}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.key} 
                  value={item.label} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={item.page} 
                />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-4">
            <button onClick={p.handleGoToWishlist} className="relative group">
              <div className="p-2 rounded-full transition-all duration-300 group-hover:bg-white/5">
                <FaHeart className={`text-lg transition-colors ${isScrolled ? "text-[#d4af37]" : "text-white"}`} />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#d4af37] text-[#1a1a2e] text-[10px] flex items-center justify-center font-bold">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={!isScrolled} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a12] border-t border-[#d4af37]/20 py-4 px-4 flex flex-col gap-3 z-50 animate-slide-down">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, null, e)}
                className={`text-sm py-2 px-3 rounded-lg transition-all ${activePage === item.page ? "bg-[#d4af37]/20 text-[#d4af37]" : "text-gray-300 hover:text-[#d4af37]"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.key} 
                  value={item.label} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={item.page} 
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section with Jewelry Imagery - Editable Images */}
          <section className="relative h-screen min-h-[750px] overflow-hidden -mt-[72px]">
            <div className="absolute inset-0">
              {jewelryHeroImages.map((img, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    idx === activeJewelryIndex ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="block h-full w-full overflow-hidden">
                    <img
                      src={p.storeTheme?.[img.field] || img.src}
                      alt={`Jewelry ${idx + 1}`}
                      className="w-full h-full object-cover object-center"
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a12]/80 via-[#0a0a12]/40 to-transparent pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-transparent to-transparent pointer-events-none" />
            </div>
            
            {/* Floating Diamond Effect */}
            <div className="absolute top-1/4 right-10 animate-float">
              <div className="relative">
                <div className="w-20 h-20 border border-[#d4af37]/30 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <div className="text-3xl animate-pulse">💎</div>
                </div>
                <div className="absolute -top-2 -right-2 w-3 h-3 bg-[#d4af37] rounded-full animate-ping" />
              </div>
            </div>
            
            {/* Hero Content */}
            <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-center">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-12 h-px bg-[#d4af37]/50" />
                  <span className="text-[#d4af37] text-sm tracking-[0.3em] font-light">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="heroEyebrow" 
                      value={copy.eyebrow} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="home" 
                    />
                  </span>
                  <div className="w-12 h-px bg-[#d4af37]/50" />
                </div>
                
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-light text-white leading-[1.1] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="home" 
                  />
                </h1>
                
                <p className="mt-6 text-gray-300 text-lg max-w-lg font-light leading-relaxed">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle} 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="home" 
                  />
                </p>
                
                <div className="mt-10 flex gap-4">
                  <button
                    onClick={(e) => navigateTo("collections", null, e)}
                    className="group px-8 py-4 rounded-full bg-[#d4af37] text-[#1a1a2e] font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#d4af37]/20 flex items-center gap-2"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="collections" 
                    />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                  </button>
                  
                  <button
                    onClick={(e) => navigateTo("about", null, e)}
                    className="px-8 py-4 rounded-full border border-[#d4af37]/40 text-white font-semibold tracking-wide transition-all duration-300 hover:border-[#d4af37] hover:bg-white/5 backdrop-blur-sm"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta} 
                      onNavigate={handleNavigateAfterEdit} 
                      navPage="about" 
                    />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Scroll Indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <div className="flex flex-col items-center gap-2">
                <span className="text-[#d4af37]/60 text-xs tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="home" 
                  />
                </span>
                <div className="w-px h-12 bg-gradient-to-b from-[#d4af37] to-transparent animate-scroll-line" />
              </div>
            </div>
          </section>
          
          {/* Category Showcase - Editable Images */}
          <section className="max-w-7xl mx-auto w-full px-4 -mt-16 z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {editableCategories.map((category, idx) => (
                <div
                  key={category.name}
                  className="group relative overflow-hidden rounded-2xl cursor-pointer"
                  onClick={(e) => navigateTo(category.page, null, e)}
                >
                  <div className="relative h-64">
                    <InlineEditableImage p={p} field={category.imageField} className="block w-full h-full overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </InlineEditableImage>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/40 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">{category.icon}</div>
                      <h3 className="text-xl font-semibold tracking-wide">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={category.key} 
                          value={category.name} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage={category.page} 
                        />
                      </h3>
                      <p className="text-sm text-white/70 mt-1">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={category.descriptionKey} 
                          value={category.description} 
                          onNavigate={handleNavigateAfterEdit} 
                          navPage={category.page} 
                        />
                      </p>
                      <div className="mt-3 flex items-center gap-1 text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-xs tracking-wider">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field="shop_now_text" 
                            value="SHOP NOW" 
                            onNavigate={handleNavigateAfterEdit} 
                            navPage={category.page} 
                          />
                        </span>
                        <span>→</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== COLLECTIONS PAGE ==================== */}
      {(activePage === "collections" || activePage === "rings" || activePage === "necklaces" || activePage === "earrings" || activePage === "bracelets") && (
        <>
          {/* Page Header */}
          <section className="relative pt-20 pb-10">
            <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e] to-transparent opacity-50 pointer-events-none" />
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-6xl font-light text-white">
                {activePage === "collections" ? 
                  <InlineEditableTextWithNav p={p} field="collections_title" value="Our Collections" onNavigate={handleNavigateAfterEdit} navPage="collections" /> :
                 activePage === "rings" ? 
                  <InlineEditableTextWithNav p={p} field="rings_title" value="Rings Collection" onNavigate={handleNavigateAfterEdit} navPage="rings" /> :
                 activePage === "necklaces" ? 
                  <InlineEditableTextWithNav p={p} field="necklaces_title" value="Necklaces Collection" onNavigate={handleNavigateAfterEdit} navPage="necklaces" /> :
                 activePage === "earrings" ? 
                  <InlineEditableTextWithNav p={p} field="earrings_title" value="Earrings Collection" onNavigate={handleNavigateAfterEdit} navPage="earrings" /> : 
                  <InlineEditableTextWithNav p={p} field="bracelets_title" value="Bracelets Collection" onNavigate={handleNavigateAfterEdit} navPage="bracelets" />}
              </h1>
              <div className="w-20 h-px bg-[#d4af37] mx-auto mt-4" />
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                {activePage === "collections" ? 
                  <InlineEditableTextWithNav p={p} field="collections_subtitle" value="Discover our exquisite range of handcrafted jewelry" onNavigate={handleNavigateAfterEdit} navPage="collections" /> :
                  <InlineEditableTextWithNav p={p} field="category_subtitle" value="Discover our stunning collection of handcrafted pieces" onNavigate={handleNavigateAfterEdit} navPage={activePage} />}
              </p>
            </div>
          </section>
          
          {/* Products Grid - Each product card is editable */}
          <section id="products-section" className="max-w-7xl mx-auto w-full px-4 py-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {displayProducts.map((product, idx) => (
                <div
                  key={product._id}
                  className="group cursor-pointer"
                  onMouseEnter={() => setHoveredProduct(product._id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1a2e] to-[#0d0d1a] hover:shadow-xl hover:shadow-[#d4af37]/10 transition-all duration-500">
                    <div className="aspect-square relative">
                      {product.productImage || product.thumbnail ? (
                        <img
                          src={AWS_URL + (product.productImage || product.thumbnail)}
                          alt={product.productName}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-6xl">💎</span>
                        </div>
                      )}
                    </div>
                    <div className="p-5 text-center">
                      <h3 className="font-light text-white tracking-wide line-clamp-1">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`product_${product._id}_name`} 
                          value={product.productName}
                          onNavigate={handleNavigateAfterEdit} 
                          navPage={activePage} 
                        />
                      </h3>
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <span className="text-[#d4af37] text-xl font-light">
                          ₹<InlineEditableTextWithNav 
                            p={p} 
                            field={`product_${product._id}_price`} 
                            value={product.salePrice?.toString() || "0"}
                            onNavigate={handleNavigateAfterEdit} 
                            navPage={activePage} 
                          />
                        </span>
                        {product.mrp > product.salePrice && (
                          <span className="text-gray-500 text-sm line-through">
                            ₹<InlineEditableTextWithNav 
                              p={p} 
                              field={`product_${product._id}_mrp`} 
                              value={product.mrp?.toString() || "0"}
                              onNavigate={handleNavigateAfterEdit} 
                              navPage={activePage} 
                            />
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="text-[#d4af37] text-xs">★</span>
                        ))}
                      </div>
                      <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProduct(product);
                          }}
                          className="px-4 py-1.5 text-xs bg-[#d4af37] text-[#1a1a2e] rounded-full font-semibold hover:bg-[#e8c45a] transition-colors"
                        >
                          <InlineEditableTextWithNav 
                            p={p} 
                            field="view_details_button" 
                            value="View Details"
                            onNavigate={handleNavigateAfterEdit} 
                            navPage={activePage} 
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== ABOUT PAGE - Editable Image ==================== */}
      {activePage === "about" && (
        <section className="max-w-7xl mx-auto w-full px-4 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-px bg-[#d4af37]" />
                <span className="text-[#d4af37] text-sm tracking-[0.2em]">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="about_badge" 
                    value="OUR STORY" 
                    onNavigate={handleNavigateAfterEdit} 
                    navPage="about" 
                  />
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-light text-white mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="The Art of Fine Jewelry" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="about" 
                />
              </h2>
              <div className="w-16 h-px bg-[#d4af37] mb-6" />
              <p className="text-gray-300 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Founded with a passion for timeless elegance, Aura Jewelry represents the pinnacle of craftsmanship and design. Each piece is meticulously crafted by master artisans using ethically sourced materials." 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="about" 
                />
              </p>
              <p className="text-gray-300 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="From initial sketch to final polish, every step reflects our commitment to excellence. We believe that jewelry is more than an accessory—it's a legacy, a memory, and a promise." 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="about" 
                />
              </p>
              <div className="grid grid-cols-2 gap-4">
                {aboutStats.map((stat) => (
                  <div key={stat.valueKey} className="border-l-2 border-[#d4af37] pl-4">
                    <p className="text-2xl font-light text-white">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={stat.valueKey} 
                        value={stat.value} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="about" 
                      />
                    </p>
                    <p className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={stat.labelKey} 
                        value={stat.label} 
                        onNavigate={handleNavigateAfterEdit} 
                        navPage="about" 
                      />
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#d4af37]/20 rounded-3xl blur-xl" />
              <InlineEditableImage p={p} field={aboutImageConfig.field} className="block w-full overflow-hidden relative rounded-2xl shadow-2xl">
                <img
                  src={p.storeTheme?.aboutImage || aboutImageConfig.src}
                  alt="Jewelry making"
                  className="w-full h-full object-cover"
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== TABS SECTION ==================== */}
      {(activePage !== "about") && (
        <section className="max-w-7xl mx-auto w-full px-4 mt-16">
          <div className="flex justify-center gap-2 border-b border-[#d4af37]/20 pb-2">
            {tabItems.map((tab) => (
              <button
                key={tab.key}
                onClick={() => p.setActiveTab(tab.key)}
                className={`px-8 py-3 text-sm tracking-wider font-light transition-all duration-300 ${
                  p.activeTab === tab.key
                    ? "text-[#d4af37] border-b-2 border-[#d4af37]"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={tab.labelKey} 
                  value={tab.label} 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                />
              </button>
            ))}
          </div>
        </section>
      )}
      
      {/* Main Content */}
      {(activePage !== "about") && (
        <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-12">
          <TabContent p={p} />
        </section>
      )}
      
      {/* ==================== NEWSLETTER SECTION ==================== */}
      {activePage !== "about" && (
        <section className="py-16">
          <div className="max-w-3xl mx-auto text-center px-4">
            <div className="inline-block mb-4">
              <span className="text-3xl">✨</span>
            </div>
            <h3 className="text-2xl font-light text-white mb-3">
              <InlineEditableTextWithNav 
                p={p} 
                field="newsletter_title" 
                value="Join the Aura Circle" 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </h3>
            <p className="text-gray-400 mb-6">
              <InlineEditableTextWithNav 
                p={p} 
                field="newsletter_subtitle" 
                value="Be the first to know about new collections, private sales, and jewelry care tips." 
                onNavigate={handleNavigateAfterEdit} 
                navPage="home" 
              />
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={p.storefrontText?.email_placeholder || "Your email address"}
                className="flex-1 px-6 py-3 rounded-full bg-[#1a1a2e] border border-[#d4af37]/20 text-white placeholder-gray-500 focus:outline-none focus:border-[#d4af37] transition-colors"
              />
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    // Handle newsletter subscription
                  }
                }}
                className="px-6 py-3 rounded-full bg-[#d4af37] text-[#1a1a2e] font-semibold hover:bg-[#e8c45a] transition-colors"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="subscribe_button" 
                  value="Subscribe" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage="home" 
                />
              </button>
            </div>
          </div>
        </section>
      )}
      
      <StoreFooter p={p} dark />
      
      {/* Product Modal - Fully Editable */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-lg z-50 flex items-center justify-center p-4" onClick={() => setSelectedProduct(null)}>
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0d0d1a] rounded-2xl max-w-md w-full overflow-hidden border border-[#d4af37]/30 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="relative h-80">
              {selectedProduct.productImage ? (
                <img 
                  src={AWS_URL + selectedProduct.productImage}
                  alt={selectedProduct.productName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-7xl">💎</div>
              )}
              <button 
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 text-white hover:bg-[#d4af37] hover:text-black transition-all duration-300"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-light text-white">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`product_${selectedProduct._id}_name`} 
                  value={selectedProduct.productName}
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                  isInModal={true}
                />
              </h3>
              <p className="text-[#d4af37] text-2xl font-light mt-2">
                ₹<InlineEditableTextWithNav 
                  p={p} 
                  field={`product_${selectedProduct._id}_price`} 
                  value={selectedProduct.salePrice?.toString() || "0"}
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                  isInModal={true}
                />
              </p>
              <p className="text-gray-400 text-sm mt-3 leading-relaxed">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`product_${selectedProduct._id}_description`} 
                  value={`Exquisite ${selectedProduct.productName} from our luxury collection. Meticulously crafted with the finest materials.`}
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                  isInModal={true}
                />
              </p>
              <div className="mt-4 text-xs text-[#d4af37] text-center bg-[#d4af37]/10 p-3 rounded-full border border-[#d4af37]/20">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="product_modal_message" 
                  value="✨ Crafted with love and precision"
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                  isInModal={true}
                />
              </div>
              <button 
                onClick={() => setSelectedProduct(null)} 
                className="w-full mt-4 py-3 bg-[#d4af37] text-[#1a1a2e] rounded-full font-semibold hover:bg-[#e8c45a] transition-colors"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_button" 
                  value="Close" 
                  onNavigate={handleNavigateAfterEdit} 
                  navPage={activePage} 
                  isInModal={true}
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        .animate-twinkle {
          animation: twinkle 2s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        @keyframes scroll-line {
          0% { transform: scaleY(0); opacity: 0; }
          50% { transform: scaleY(1); opacity: 1; }
          100% { transform: scaleY(0); opacity: 0; }
        }
        .animate-scroll-line {
          animation: scroll-line 2s ease-in-out infinite;
          transform-origin: top;
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}